import { Box, Skeleton } from '@mui/material';
import './markdown-overrides.css';
import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation } from 'react-router-dom';
import rehypeSlug from 'rehype-slug';
import remarkDirective from 'remark-directive';
import remarkToc from 'remark-toc';

import { BackToTop } from 'shared/ui/BackToTop';

import {
  MarkdownLink,
  createReactLink,
  markdownDirectivePlugin,
  rehypeTocStyler,
  scrollToAnchor,
} from './MarkdownUtils';

interface MarkdownDisplayProps {
  markdown: string;
  isLoading: boolean;
  sellerId?: string;
}

const MarkdownDisplaySkeleton = () => {
  return (
    <Box sx={{ flex: 1, width: '100%', maxWidth: '1460px' }}>
      <Skeleton
        variant="rectangular"
        sx={{ width: '40%', height: '40px', marginBottom: '15px' }}
      />
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}
        >
          <Skeleton
            variant="text"
            sx={{ width: 'calc(100% - 90px)', height: '30px' }}
          />
        </Box>
      ))}
    </Box>
  );
};

const MarkdownDisplay = ({
  markdown,
  isLoading,
  sellerId,
}: MarkdownDisplayProps) => {
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && markdown) {
      requestAnimationFrame(() => {
        scrollToAnchor();
      });
    }
  }, [isLoading, markdown, location.hash]);

  if (isLoading) {
    return <MarkdownDisplaySkeleton />;
  }

  if (!markdown) {
    return null;
  }

  return (
    <Box className="markdown" sx={{ width: '100%', maxWidth: '1460px' }}>
      <ReactMarkdown
        remarkPlugins={[
          remarkDirective,
          markdownDirectivePlugin,
          [remarkToc, { heading: 'Table of contents' }],
        ]}
        rehypePlugins={[rehypeSlug, rehypeTocStyler]}
        components={
          {
            a: MarkdownLink,
            ReactLink: createReactLink(sellerId),
          } as any
        }
      >
        {markdown}
      </ReactMarkdown>
      <BackToTop containerId="right_side_page" />
    </Box>
  );
};

export { MarkdownDisplay };
